import React, { useState } from 'react';
import { Keyboard } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useGlobalShortcut, useRegisteredShortcuts } from '@/hooks/useGlobalShortcut';
import { formatCombo } from '@/lib/globalShortcuts';

/**
 * Mounts the global "Keyboard Shortcuts" help dialog plus the `?` shortcut
 * that opens it. Drop one <KeyboardShortcutsDialog /> near the App root and
 * any registered shortcut becomes discoverable.
 *
 * The dialog itself is also a registered shortcut (id="global.help") so it
 * shows up in its own list, demonstrating the registry works.
 */
export function KeyboardShortcutsDialog() {
  const [open, setOpen] = useState(false);
  const shortcuts = useRegisteredShortcuts();

  useGlobalShortcut({
    id: 'global.help',
    keys: '?',
    label: 'Show keyboard shortcuts',
    category: 'General',
    handler: () => setOpen(o => !o),
    allowInInputs: false,
  });

  // Group by category, alphabetised within each.
  const byCategory = {};
  for (const sc of shortcuts) {
    (byCategory[sc.category] = byCategory[sc.category] || []).push(sc);
  }
  const categories = Object.keys(byCategory).sort();
  for (const c of categories) byCategory[c].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-auto" data-testid="keyboard-shortcuts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Keyboard className="w-4 h-4 text-primary" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription className="text-xs">
            Press <kbd className="px-1.5 py-0.5 rounded bg-muted/40 border border-border/40 text-[10px] font-mono">?</kbd> any time to toggle this list.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {categories.length === 0 && (
            <p className="text-xs text-muted-foreground italic text-center py-6">
              No shortcuts registered yet. Open a page with editing tools (e.g. the Report Builder) to see its shortcuts here.
            </p>
          )}
          {categories.map(cat => (
            <div key={cat} data-testid={`shortcut-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">{cat}</p>
              <ul className="space-y-1">
                {byCategory[cat].map(sc => (
                  <li
                    key={sc.id}
                    className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-md hover:bg-muted/20 transition-colors"
                    data-testid={`shortcut-row-${sc.id}`}
                  >
                    <span className="text-xs text-foreground">{sc.label}</span>
                    <span className="flex items-center gap-1.5 flex-shrink-0">
                      {sc.keys.map((combo, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-[9px] text-muted-foreground/80">or</span>}
                          <kbd className="px-2 py-0.5 rounded-md bg-muted/40 border border-border/40 text-[10px] font-mono text-foreground/90">
                            {formatCombo(combo)}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
